import { buildStyleString } from './reflectEditor';

export const BOOTH_SONGS_KEY = 'sp_booth_songs_v1';
export const BOOTH_OPEN_SONG_KEY = 'sp_booth_open_song';
export const BOOTH_SONG_WARNING_COUNT = 80;

export const VIBE_SEEDS = {
  'big-energy': { chips: ['high-energy hip-hop', 'pop', 'bright synths'], bpm: 125 },
  'bounce-vibe': { chips: ['bouncy hip-hop', 'punchy drums'], bpm: 100 },
  'calm-focus': { chips: ['lo-fi hip-hop', 'calm', 'dusty keys'], bpm: 80 },
  'trap-energy': { chips: ['trap', 'hard 808s', 'fast hi-hats'], bpm: 140 },
  'afro-chill': { chips: ['afrobeats', 'smooth', 'warm percussion'], bpm: 105 },
  'warm-rnb': { chips: ['R&B', 'warm', 'soulful'], bpm: 80 },
  'dark-drill': { chips: ['drill', 'sliding 808s', 'dark'], bpm: 142 },
  'latin-pop': { chips: ['Latin pop', 'bright', 'danceable'], bpm: 115 },
  'latin-warm': { chips: ['Latin acoustic', 'guitar', 'warm percussion'], bpm: 95 },
  'reggaeton-bounce': { chips: ['reggaeton', 'dembow groove'], bpm: 95 },
  'playful-pop': { chips: ['playful pop', 'bouncy'], bpm: 118 },
  'confident-grind': { chips: ['boom-bap', 'confident', 'piano and drums'], bpm: 90 },
};

const makeId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `song-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isSong = (song) => (
  song
  && typeof song === 'object'
  && typeof song.id === 'string'
  && typeof song.title === 'string'
  && typeof song.style === 'string'
  && Array.isArray(song.cadence)
  && Array.isArray(song.sections)
);

const recover = (storage, raw) => {
  try {
    storage.setItem(`${BOOTH_SONGS_KEY}_corrupt_${Date.now()}`, raw);
  } catch (_error) {
    // Recovery still returns the raw text when storage cannot accept the backup.
  }
  return { songs: [], recovered: raw };
};

export const loadSongs = (storage) => {
  let raw;
  try {
    raw = storage.getItem(BOOTH_SONGS_KEY);
  } catch (_error) {
    return { songs: [], recovered: null };
  }
  if (raw === null) return { songs: [], recovered: null };
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1 || !Array.isArray(parsed.songs)) return recover(storage, raw);
    // One damaged song never takes the others down: keep the good ones, back up the rest.
    const good = parsed.songs.filter(isSong);
    if (good.length === parsed.songs.length) return { songs: good, recovered: null };
    const bad = parsed.songs.filter((song) => !isSong(song));
    return { songs: good, recovered: recover(storage, JSON.stringify(bad)).recovered };
  } catch (_error) {
    return recover(storage, raw);
  }
};

// What another tab saved, read without side effects. Damaged data reads as nothing.
export const readStoredSongs = (storage) => {
  try {
    const parsed = JSON.parse(storage.getItem(BOOTH_SONGS_KEY) || 'null');
    return Array.isArray(parsed?.songs) ? parsed.songs.filter(isSong) : [];
  } catch (_error) {
    return [];
  }
};

// Two tabs share one list. Per song, the newest edit wins; songs only the other tab has are
// kept unless this tab deleted them. Order follows this tab, then the other tab's new songs.
export const mergeSongs = (stored = [], mine = [], deletedIds = new Set()) => {
  const storedById = new Map(stored.map((song) => [song.id, song]));
  const merged = mine.map((song) => {
    const other = storedById.get(song.id);
    return other && String(other.updated_at) > String(song.updated_at) ? other : song;
  });
  const mineIds = new Set(mine.map((song) => song.id));
  stored.forEach((song) => {
    if (!mineIds.has(song.id) && !deletedIds.has(song.id)) merged.push(song);
  });
  return merged;
};

export const saveSongs = (storage, songs) => {
  try {
    storage.setItem(BOOTH_SONGS_KEY, JSON.stringify({ version: 1, songs }));
    return { ok: true };
  } catch (error) {
    const full = error?.name === 'QuotaExceededError'
      || error?.code === 22
      || error?.code === 1014;
    return { ok: false, reason: full ? 'full' : 'error' };
  }
};

export const newSong = (vibe = null) => {
  const seed = VIBE_SEEDS[vibe] || { chips: ['hip-hop'], bpm: 90 };
  const now = new Date().toISOString();
  return {
    id: makeId(),
    title: 'Untitled',
    vibe: vibe || null,
    style: buildStyleString(seed.chips, seed.bpm, false),
    cadence: [],
    sections: [
      { label: 'Hook', lines: [{ text: '' }] },
      { label: 'Verse 1', lines: [{ text: '' }] },
    ],
    beat: null,
    created_at: now,
    updated_at: now,
  };
};

export const duplicateSong = (song) => {
  const now = new Date().toISOString();
  return {
    ...JSON.parse(JSON.stringify(song)),
    id: makeId(),
    title: `${song.title || 'Untitled'} copy`.slice(0, 80),
    created_at: now,
    updated_at: now,
  };
};

export const renameSong = (song, title) => ({
  ...song,
  title: String(title || '').slice(0, 80) || 'Untitled',
  updated_at: new Date().toISOString(),
});

export const removeSong = (songs, id) => songs.filter((song) => song.id !== id);

export const songToText = (song = {}) => {
  const parts = [song.title || 'Untitled', `Style: ${song.style || ''}`];
  (song.sections || []).forEach((section) => {
    parts.push(`[${section.label}]\n${(section.lines || []).map((line) => line.text).join('\n')}`);
  });
  const flow = (song.cadence || [])
    .filter((item) => String(item.note || '').trim())
    .map((item) => `${item.label}: ${item.note}`);
  if (flow.length) parts.push(`Flow notes:\n${flow.join('\n')}`);
  return `${parts.join('\n\n')}\n`;
};
